import axios from 'axios';

// 创建axios实例
const api = axios.create({
  baseURL: '/api', // 使用proxy配置，指向后端服务器
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从本地存储获取令牌
    const token = localStorage.getItem('auth_token');
    
    // 如果有令牌，则添加到请求头
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // 如果是认证错误，跳转到登录页面
    if (error.response && error.response.status === 401) {
      // 清除本地存储的令牌
      localStorage.removeItem('auth_token');
      
      // 如果不是登录页面，则跳转到登录页面
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    // 对于409错误（工作流已存在），需要保留状态码信息
    if (error.response && error.response.status === 409) {
      const errorData = error.response.data || {};
      return Promise.reject({
        ...errorData,
        status: error.response.status
      });
    }
    
    return Promise.reject(error.response?.data || error);
  }
);

// 认证相关API
const authAPI = {
  // 登录
  login: (password) => {
    return api.post('/auth/login', { password });
  },
  
  // 验证令牌
  verifyToken: () => {
    return api.post('/auth/verify');
  },
  
  // 检查是否已登录
  isLoggedIn: () => {
    return !!localStorage.getItem('auth_token');
  },
  
  // 登出
  logout: () => {
    localStorage.removeItem('auth_token');
  }
};

// 工作流相关API
const workflowAPI = {
  // 获取工作流列表 - 支持分页和搜索
  getAllWorkflows: (page = 1, limit = 10, search = '') => {
    const params = new URLSearchParams({ 
      page: page.toString(), 
      limit: limit.toString() 
    });
    if (search) {
      params.append('search', search);
    }
    return api.get(`/workflows?${params.toString()}`);
  },
  
  // 按工作流编号搜索
  searchWorkflowByNum: (workflowNum) => {
    return api.get(`/workflows/search/${workflowNum}`);
  },
  
  // 上传工作流
  uploadWorkflow: (formData, customUrl = '/workflows/upload') => {
    return api.post(customUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 批量上传工作流
  batchUploadWorkflows: (formData) => {
    return api.post('/workflows/batch-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 获取工作流详情
  getWorkflowDetail: (workflowNum) => {
    return api.get(`/workflows/${workflowNum}`);
  },
  
  // 上传工作流封面图
  uploadWorkflowCover: (workflowNum, coverImageFile) => {
    const formData = new FormData();
    formData.append('cover_image', coverImageFile);
    
    return api.post(`/workflows/${workflowNum}/cover`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 保存工作流修改
  saveWorkflowModifications: (workflowNum, modifications) => {
    return api.post(`/workflows/${workflowNum}/save`, {
      modifications
    });
  },
  
  // 上传图片到OSS
  uploadImage: (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    return api.post('/workflows/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 修改工作流名称
  updateWorkflowName: (workflowNum, workflowName) => {
    return api.put(`/workflows/${workflowNum}/name`, {
      workflow_name: workflowName
    });
  },
  
  // 删除工作流
  deleteWorkflow: (workflowNum) => {
    return api.delete(`/workflows/${workflowNum}`);
  },
  
  // 复制工作流（基于模板创建新工作流）
  cloneWorkflow: (sourceWorkflowNum, newWorkflowNum, newWorkflowName) => {
    return api.post(`/workflows/${sourceWorkflowNum}/clone`, {
      workflow_num: newWorkflowNum,
      workflow_name: newWorkflowName
    });
  },
  
  // 更新工作流小程序展示状态
  updateMiniprogramDisplay: (workflowNum, miniprogramDisplay) => {
    return api.put(`/workflows/${workflowNum}/miniprogram-display`, {
      miniprogram_display: miniprogramDisplay
    });
  }
};

export { api, workflowAPI, authAPI }; 